import React, { useEffect, useState, useContext } from 'react';
import { useAuth } from '@teko/auth-react';
import { useTenantConfig } from 'hooks';
import { PageTypeEnum } from 'types';
import {
  FCM_TOKEN,
  FCM_USER_ID,
  Notification as Noti,
} from '@teko/notification';
import { usePersonalizeBlocks } from 'hooks/useDiscovery';
import { useFeatureFlags, useServices } from 'contexts';
import { initializeApp } from 'firebase/app';
import { getFcmToken, registerFcmToken } from 'helpers';

export enum PageStatus {
  LOADING = 'loading',
  LOADED = 'loaded',
  EMPTY = 'empty',
  FAILED = 'failed',
}

interface NotificationProviderProps {
  appendData?: boolean;
  children: React.ReactNode;
}

interface INotificationContext {
  status: PageStatus;
  notifications: any[];
  promotions: any[];
  checkpoint?: any;
  loading: boolean;
  getNotifications: (checkpoint: number | undefined) => void;
  handleNotificationClicked: (notification: any) => void;
}

const isJSON = (str: string) => {
  try {
    return !!str && JSON.parse(str);
  } catch (e: any) {
    return false;
  }
};

export const NotificationContext = React.createContext<
  INotificationContext | undefined
>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used in NotificationProvider');
  }

  return context;
};

export const NotificationProvider = ({
  appendData = false,
  children,
}: NotificationProviderProps) => {
  const { user, loading: userLoading } = useAuth();
  const { notificationService } = useServices();
  const { notificationAppId: app_id, getProcessEnv } = useTenantConfig();
  const { result: blocks, isLoading: loadingPromotion } = usePersonalizeBlocks({
    pageTypeEnum: PageTypeEnum.NOTIFICATION,
  });
  const firebaseConfig = {
    apiKey: getProcessEnv('FIREBASE_CLOUD_MESSAGING_API_KEY'),
    authDomain: getProcessEnv('FIREBASE_CLOUD_MESSAGING_AUTH_DOMAIN'),
    databaseURL: getProcessEnv('FIREBASE_CLOUD_MESSAGING_DATABASE_URL'),
    projectId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_PROJECT_ID'),
    storageBucket: getProcessEnv('FIREBASE_CLOUD_MESSAGING_STORAGE_BUCKET'),
    messagingSenderId: getProcessEnv(
      'FIREBASE_CLOUD_MESSAGING_MESSAGING_SENDER_ID',
    ),
    appId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_APP_ID'),
    measurementId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_MEASUREMENT_ID'),
  };
  initializeApp(firebaseConfig);

  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADED);
  const [notifications, setNotifications] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [checkpoint, setCheckpoint] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const { flags } = useFeatureFlags();

  const getNotifications = async (checkpoint: number = undefined) => {
    const token = localStorage.getItem('fcmToken');
    const params = {
      app_id,
      limit: 10,
      ignore_pending_send: 0,
      check_point: checkpoint,
      token: token ?? undefined,
    };
    setLoading(true);
    return notificationService
      .getNotification(params)
      .then((res) => {
        let formatedNotis = [];
        if (res) {
          if (!!res.notifications.length) {
            formatedNotis = res.notifications.map((noti: Noti) => {
              const link =
                noti.link && isJSON(noti.link) ? JSON.parse(noti.link) : '';
              if (link && ['order', 'noti'].includes(link.type)) {
                return {
                  ...noti,
                  order: link.type === 'order' ? link?.data : null,
                  deepLink: link.type === 'noti' ? link?.data?.deeplink : null,
                };
              }
            });
            if (appendData) {
              setNotifications([...notifications, ...formatedNotis]);
            } else {
              setNotifications(formatedNotis);
            }
          }
          setCheckpoint(res.check_point);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const installWebPushNotification = async () => {
    // implement sequential 2 steps
    try {
      // Step 1: register token and save firebase token in localStorage.getItem('fcmToken');
      if (flags.epic_omni_633) {
        if (
          'serviceWorker' in navigator &&
          typeof Notification !== 'undefined'
        ) {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            const fcmToken = await getFcmToken(
              getProcessEnv('FIREBASE_VAPID_KEY'),
            );
            registerFcmToken(fcmToken, user, app_id, notificationService);
          }
        }
      } else {
        localStorage.removeItem(FCM_TOKEN);
        localStorage.removeItem(FCM_USER_ID);
      }
    } finally {
      // Step 2: use firebase token to get notificaations
      getNotifications(checkpoint);
    }
  };

  useEffect(() => {
    if (!userLoading) {
      installWebPushNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, userLoading]);

  useEffect(() => {
    if (!loading && !loadingPromotion) {
      const promotions =
        blocks.filter((block: any) => block.type == 'SMALL_MIX_NEWS')?.[0]
          ?.content?.items || [];
      if (promotions.length > 0) {
        setPromotions(promotions);
      }
      if (notifications.length === 0 && promotions.length === 0) {
        setStatus(PageStatus.EMPTY);
      } else {
        setStatus(PageStatus.LOADED);
      }
    } else {
      setStatus(PageStatus.LOADING);
    }
  }, [blocks, loading, loadingPromotion]);

  const handleNotificationClicked = (notification: any) => {
    if (!notification.is_read) {
      notificationService
        .markNotificationAsRead(notification.id)
        .then((res: any) => {
          if (res?.status === 'success') {
            setNotifications((prev) =>
              prev.map((noti: Noti) => {
                if (noti.id === notification.id) {
                  return {
                    ...noti,
                    is_read: true,
                  };
                }
                return noti;
              }),
            );
          }
        });
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        status,
        notifications,
        promotions,
        checkpoint,
        loading: loading || loadingPromotion,
        getNotifications,
        handleNotificationClicked,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
